import React, {useState, useEffect} from "react";
import {
    Box,
    Text,
    Textarea,
    Button,
    useColorModeValue,
    useToast,
    Flex,
    Spinner,
    Stack,
    Select,
} from "@chakra-ui/react";
import {cqAPI} from "../../lib/Auth";

// Sent ONCE. These writes are not idempotent: saving the prompts INSERTS a new version
// and the responder reads the newest, and posting an article inserts another one. The
// transport repeats a write whose answer was lost, so a blip could put a superseded
// configuration back on top of a colleague's newer one — every AI turn afterwards using
// a prompt nobody chose — or leave two copies of the same article in the knowledge base.
// A person watching a failure can decide to try again; the transport cannot.
const SAVE_ONCE = { attempts: 1 };
// A bound on every write from this page. `Auth.js` deliberately leaves writes unbounded by
// default, because a timed-out POST is retried by its loop and only an idempotent caller can
// afford that — but these already ask for a single attempt, so a deadline costs nothing and
// buys the one thing missing: a request the server accepted and never finished answering
// leaves `saving` true forever, the button disabled, and the author unable to learn whether
// their work was stored.
const WRITE_TIMEOUT_MS = 120000;


const SupportPrompts = () => {
    const bgColor = useColorModeValue("gray.100", "gray.700");
    const cardBg = useColorModeValue("#ffffff", "#212330");
    const borderClr = useColorModeValue("gray.200", "gray.600");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // The fields a person can type into. Identity (`_id`, `version`, `created`) is the
    // server's and is always adopted; these are the ones an in-flight save must not
    // overwrite if they have changed since it was sent.
    const EDITABLE_FIELDS = [
        "system_prompt", "fallback_reply", "escalation_ack_reply",
        "unsure_reply", "llm_provider", "model_size",
    ];

    const defaultFormData = {
        system_prompt: "",
        fallback_reply: "",
        escalation_ack_reply: "",
        unsure_reply: "",
        llm_provider: "grok",
        model_size: "large",
    };

    const [formData, setFormData] = useState(defaultFormData);
    // A read that failed leaves this page showing defaults it must not let anybody save
    // over the live configuration. The server refuses such a save regardless — it is the
    // one that owns the rule — and this keeps the author from being told "somebody else
    // saved" when what really happened is that the page never loaded.
    const [loadFailed, setLoadFailed] = useState(false);
    const toast = useToast();

    // ----------- LOAD PROMPTS -----------
    useEffect(() => {
        const fetchSupportPrompts = async () => {
            setLoading(true);
            try {
                const response = await cqAPI.get("/prompts/support");
                if (response?.type === "ok" && response.data) {
                    setFormData({...defaultFormData, ...response.data});
                } else if (response?.type === "ok") {
                    // Nothing saved yet — a successful answer with no document. Start
                    // from the defaults and let this environment save its first prompts.
                    setFormData(defaultFormData);
                } else {
                    // The read FAILED. Not the same as "nothing saved yet", and the
                    // difference matters: a form built from the defaults looks exactly
                    // like the live configuration, and saving it would replace the
                    // provider, model and canned replies every customer's assistant is
                    // running. Say so, and do not offer that form as if it were current.
                    setLoadFailed(true);
                    toast({
                        title: "Error",
                        description: response?.msg || "Failed to load Support Prompts.",
                        status: "error",
                        duration: 6000,
                        isClosable: true
                    });
                }
            } catch (error) {
                console.error("Error loading Support Prompts:", error);
                setLoadFailed(true);
                toast({
                    title: "Error",
                    description: "Failed to load Support Prompts.",
                    status: "error",
                    duration: 3000,
                    isClosable: true
                });
            } finally {
                setLoading(false);
            }
        };
        fetchSupportPrompts();
    }, []);

    // ----------- SAVE PROMPTS -----------
    const handleSave = async () => {
        setSaving(true);
        // What is being saved, as it stood when Save was pressed. The fields stay editable
        // while the request runs, and replacing the form wholesale when the answer arrives
        // erases whatever was typed in between — silently, because the toast says "Saved".
        const sent = formData;
        const edited = (current) => EDITABLE_FIELDS.some((k) => current[k] !== sent[k]);
        try {
            const response = await cqAPI.put("/prompts/support", formData, WRITE_TIMEOUT_MS, SAVE_ONCE);
            if (response?.type === "ok") {
                if (response.data) {
                    // Untouched: take the stored document whole. Edited since: keep their
                    // words and take only the IDENTITY, so the version guard still names
                    // the document that is now live and their next save is not refused
                    // against a version they never saw.
                    setFormData((current) => (edited(current)
                        ? { ...current, _id: response.data._id, version: response.data.version, created: response.data.created }
                        : { ...defaultFormData, ...response.data }));
                }
                toast({
                    title: "Saved",
                    description: "Support Prompts updated successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true
                });
            } else {
                toast({
                    title: "Error",
                    description: response?.msg || 'Failed to save!',
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error("Error saving Support Prompts:", error);
            toast({
                title: "Error",
                description: "Failed to save Support Prompts.",
                status: "error",
                duration: 3000,
                isClosable: true
            });
        } finally {
            setSaving(false);
        }
    };

    // ----------- RENDER UI -----------
    return (
        <Box bg={bgColor} minH="100dvh" w="100%">
            <Box bg={bgColor} p={6} borderRadius="md" w="100%" maxW="100%">
                <Flex justify="space-between" align="center" mb={6}>
                    <Text fontSize="xl" fontWeight="bold">
                        Support Prompts
                    </Text>
                    <Button
                        colorScheme="blue"
                        onClick={handleSave}
                        isLoading={saving}
                        isDisabled={loadFailed}
                        title={loadFailed ? "Reload the page: these prompts could not be loaded" : undefined}
                        loadingText="Saving"
                    >
                        Save
                    </Button>
                </Flex>

                {loading ? (
                    <Flex justify="center" align="center" h="200px">
                        <Spinner size="xl"/>
                    </Flex>
                ) : (
                    <Stack spacing={6}>
                        {/* Model */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Model
                            </Text>
                            <Flex gap={4} wrap="wrap">
                                <Box>
                                    <Text fontSize="sm" mb={1} textAlign="left">LLM Provider</Text>
                                    <Select
                                        w="200px"
                                        value={formData.llm_provider || "grok"}
                                        onChange={(e) => setFormData({...formData, llm_provider: e.target.value})}
                                    >
                                        <option value="grok">Grok</option>
                                        <option value="anthropic">Anthropic</option>
                                        <option value="openai">OpenAI</option>
                                    </Select>
                                </Box>
                                <Box>
                                    <Text fontSize="sm" mb={1} textAlign="left">Model Size</Text>
                                    <Select
                                        w="200px"
                                        value={formData.model_size || "large"}
                                        onChange={(e) => setFormData({...formData, model_size: e.target.value})}
                                    >
                                        <option value="large">Large</option>
                                        <option value="small">Small</option>
                                    </Select>
                                </Box>
                            </Flex>
                        </Box>

                        {/* System Prompt */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                System Prompt
                            </Text>
                            <Textarea
                                minH="360px"
                                resize="vertical"
                                value={formData.system_prompt || ""}
                                onChange={(e) => setFormData({...formData, system_prompt: e.target.value})}
                                placeholder="System Prompt..."
                            />
                        </Box>

                        {/* Fallback Reply */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Fallback Reply
                            </Text>
                            <Textarea
                                minH="120px"
                                resize="vertical"
                                value={formData.fallback_reply || ""}
                                onChange={(e) => setFormData({...formData, fallback_reply: e.target.value})}
                                placeholder="Sent when the assistant cannot answer..."
                            />
                        </Box>

                        {/* Escalation Ack Reply */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Escalation Acknowledgement Reply
                            </Text>
                            <Textarea
                                minH="120px"
                                resize="vertical"
                                value={formData.escalation_ack_reply || ""}
                                onChange={(e) => setFormData({...formData, escalation_ack_reply: e.target.value})}
                                placeholder="Sent when a ticket is handed over to a human..."
                            />
                        </Box>

                        {/* Unsure Reply */}
                        <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                            <Text fontWeight="semibold" mb={2}>
                                Not-Confident Reply
                            </Text>
                            <Text fontSize="sm" color="gray.500" mb={2} textAlign="left">
                                Sent instead of the assistant&apos;s answer whenever it is not confident.
                                The unanswered ticket is escalated and the team is notified.
                            </Text>
                            <Textarea
                                minH="120px"
                                resize="vertical"
                                value={formData.unsure_reply || ""}
                                onChange={(e) => setFormData({...formData, unsure_reply: e.target.value})}
                                placeholder="Sent when the assistant is not sure enough to answer..."
                            />
                        </Box>
                    </Stack>
                )}
            </Box>
        </Box>
    );
};

export default SupportPrompts;
